import type { ReactNode } from 'react'
import { StyleSheet, View } from 'react-native'

import { useDepositActionControlFill } from '../../components/DepositActionMotion'
import { buttonRadius } from '../../theme/buttonRadius'
import { useTheme } from '../../theme/restyle'
import type { SurfaceContext } from './adapter'

// The ring is drawn inside the frame, so its radius trails the frame's by the
// inset it sits at.
const EDGE_INSET = 2

/**
 * The slot a provider-drawn control (an Apple Pay button in a WebView) is
 * clipped into, shaped like the button it stands in for: the theme's button
 * height and radius, so a brand that squares its buttons off squares this one
 * off with them.
 *
 * `fill` paints the corners the hosted control rounds off — it must match that
 * control's own background, since the provider owns everything inside.
 *
 * Drawn at the large button's size, which an adapter cannot ask its host for:
 * inline, `SurfaceSlot` must be given the same one (`SURFACE_MATCHED_SIZE`) or
 * its clip and this frame's disagree.
 */
export function HostedControlFrame({
  fill,
  edgeColor,
  placement,
  children,
}: {
  fill: string
  /** Hairline ring for a control whose fill matches the surface behind it. */
  edgeColor?: string
  placement: SurfaceContext['placement']
  children: ReactNode
}) {
  // Inline, this fill is what the action's slot wears once the slot's cover
  // comes off, so the phase after it crosses from the provider's own colour.
  useDepositActionControlFill(placement === 'INLINE' ? fill : null)
  const theme = useTheme()
  const height = theme.sizes.buttonLg
  const radius = buttonRadius(theme, height)

  return (
    <View
      style={[
        styles.frame,
        { height, borderRadius: radius, backgroundColor: fill },
        placement === 'SCREEN' && styles.screen,
      ]}
    >
      {children}
      {edgeColor === undefined ? null : (
        <View
          pointerEvents="none"
          style={[
            styles.edge,
            {
              borderColor: edgeColor,
              borderRadius: Math.max(0, radius - EDGE_INSET),
            },
          ]}
        />
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  frame: { width: '100%', overflow: 'hidden' },
  edge: {
    position: 'absolute',
    top: EDGE_INSET,
    right: EDGE_INSET,
    bottom: EDGE_INSET,
    left: EDGE_INSET,
    borderWidth: StyleSheet.hairlineWidth * 2,
  },
  screen: { marginHorizontal: 16, width: 'auto' },
})
